import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Button, FormMessage, Icon } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { Footer, Header } from '../components';

const Container = styled.div`
  flex: 1;
  width: 49.5rem;
  max-width: 90%;
  margin: 4.5rem auto;
`;

const Box = styled.div`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0px 3px 45px 0px ${colors.grey10};
  padding: 3.3125rem 13% 2.78rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 2.25rem;
  font-weight: ${weights.light};
  text-align: center;
`;

const Byline = styled.div`
  padding-top: 0.5rem;
  font-size: 1.25rem;
  line-height: 2rem;
  color: ${colors.grey55};
  text-align: center;
  margin-bottom: 2rem;
`;

const Tile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 1.5rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.75rem;
  overflow: hidden;
`;

const TileImage = styled.img`
  width: 2.25rem;
  height: 2.25rem;
  object-fit: contain;
`;

const FormAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${Button} {
    width: 14.6875rem;
    margin: 0 auto 1rem;
  }
`;

export default function MicrosoftSignIn() {
  useDocumentTitle('Sign in with Microsoft');

  const { workspaceKey } = useParams();
  const api = useApi();
  const { navigateWorkspace } = useWorkspace();
  const [signIn, setSignIn] = useState({ status: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.microsoftSignIn(workspaceKey);
        if (data.signedIn) {
          navigateWorkspace(workspaceKey);
          return;
        }
        setSignIn({ status: 'ready', ...data });
      } catch (error) {
        setSignIn({ status: 'error', message: error.message });
      }
    })();
  }, [api, workspaceKey, navigateWorkspace]);

  const handleSignIn = async () => {
    setIsSubmitting(true);
    try {
      const { data } = await api.www.authProviders(signIn.authProviderId).authorizeUrl();
      window.location = data.authorizeUrl;
    } catch (error) {
      setSignIn({ status: 'error', message: error.message });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <Container>
        <Box>
          {{
            loading: () => (
              <>
                <Title>Login</Title>
                <Icon icon="spinner" spin />
              </>
            ),
            ready: () => (
              <>
                <Tile>
                  {signIn.workspaceImageUrl ? (
                    <TileImage src={signIn.workspaceImageUrl} alt={signIn.workspaceName} />
                  ) : (
                    <Avatar value={{ name: signIn.workspaceName.charAt(0) }} size={36} isCircle hasBackground />
                  )}
                </Tile>
                <Title>
                  Sign in to <strong>{signIn.workspaceName}</strong>
                </Title>
                <Byline>Use your Microsoft account.</Byline>
                <FormAction>
                  <Button isLoading={isSubmitting} onClick={handleSignIn}>
                    Sign in with Microsoft
                  </Button>
                  <Link to="/login">Use another method</Link>
                </FormAction>
              </>
            ),
            error: () => (
              <>
                <Title>Login</Title>
                <FormMessage.Error spaceTop>{signIn.message}</FormMessage.Error>
                <FormAction>
                  <Link to="/login">Use another method</Link>
                </FormAction>
              </>
            ),
          }[signIn.status]()}
        </Box>
      </Container>
      <Footer />
    </>
  );
}
